import { ApiResponse } from './api';
import { Benchmark } from './benchmark';
import { FundManager } from './fundManager';
import { IndexFund } from './indexFund';

export enum PortfolioStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export interface PortfolioCurrencyOption {
  id: number;
  name: string;
}

export interface PortfolioStatusOption {
  id: number;
  name: PortfolioStatus;
}

export enum PortfolioFileType {
  FACT_SHEET = 'Fact Sheet',
  FACT_SHEET_AR = 'Fact Sheet Ar',
  DISCLOSURE = 'Disclosure',
  OTHER = 'Other',
}

export interface PortfolioFileTypeOption {
  id: number;
  name: PortfolioFileType;
}

export interface PortfolioProductTypeOption {
  id: number;
  nameEn: string;
  nameAr: string;
  disabled: boolean;
  scoreDependent: boolean;
  isCustom: boolean;
  minimumPortfolioInvestment: number;
  minimumAddFundInvestment: number;
  minimumRedemption: number;
  fee: number;
  descriptionEn: string;
  descriptionAr: string;
}

export interface PortfolioUserCategoryOption {
  id: number;
  userCategory: {
    id: number;
    name: string;
  };
}

export interface PortfolioFile {
  id: number;
  portfolioFileType: PortfolioFileTypeOption;
  fileId: string;
  isPublic: boolean;
}

export interface PortfolioAssociatedIndexFund {
  id: number;
  indexFund: IndexFund;
  weight: number;
}

export type Portfolio = {
  id: number;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  objectiveEn: string;
  objectiveAr: string;
  shariaaComplaint: boolean;
  createdBy: number;
  expectedMinimumReturn: number;
  targetExpectedReturn: number;
  expectedMaximumReturn: number;
  targetExpectedRisk: number;
  benchmark: Benchmark;
  sharpRatio: number;
  displayByGics: boolean;
  isSectorBased: boolean;
  userCategory: PortfolioUserCategoryOption[];
  status: PortfolioStatusOption;
  currency: PortfolioCurrencyOption;
  productType: PortfolioProductTypeOption;
  manager: FundManager;
  files: PortfolioFile[];
  associatedIndexFunds: PortfolioAssociatedIndexFund[];
  logoUrl?: string;
  backgroundUrl?: string;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export type FetchPortfolioResponse = ApiResponse<Portfolio>;
export type FetchPortfoliosResponse = ApiResponse<{
  items: Portfolio[];
  totalItems: number;
}>;
export type FetchPortfolioCurrencyOptionsResponse = ApiResponse<PortfolioCurrencyOption[]>;
export type FetchPortfolioStatusOptionsResponse = ApiResponse<PortfolioStatusOption[]>;
export type FetchPortfolioFileTypeOptionsResponse = ApiResponse<PortfolioFileTypeOption[]>;

export type UpdatePortfolioStatusRequest = Pick<Portfolio, 'id'> & { status: PortfolioStatus };
export type UpdatePortfolioStatusResponse = ApiResponse<{ message: string }>;

export type SavePortfolioRequest = Omit<
  Portfolio,
  | 'id'
  | 'status'
  | 'currency'
  | 'manager'
  | 'files'
  | 'associatedIndexFunds'
  | 'userCategory'
  | 'createdBy'
  | 'benchmark'
  | 'createdAt'
  | 'updatedAt'
  | 'productType'
> & {
  id?: number;
  files: { portfolioFileType: number; fileId: string; isPublic: boolean }[];
  associatedIndexFunds: { indexFund: number; weight: number }[];
  userCategory: number[];
  status: number;
  manager: number;
  currency: number;
  benchmark: number;
  productType: number;
  logoUrl?: string;
  backgroundUrl?: string;
};
export type CreatePortfolioResponse = ApiResponse<Portfolio>;

export type UpdatePortfolioRequestFile = { id?: number; portfolioFileType: number; fileId: string; isPublic: boolean };
export type UpdatePortfolioRequestAssociatedIndexFund = { id?: number; indexFund: number; weight: number };
export type UpdatePortfolioRequest = Omit<
  Portfolio,
  | 'status'
  | 'createdBy'
  | 'currency'
  | 'manager'
  | 'files'
  | 'associatedIndexFunds'
  | 'userCategory'
  | 'benchmark'
  | 'createdAt'
  | 'updatedAt'
> & {
  files: UpdatePortfolioRequestFile[];
  associatedIndexFunds: UpdatePortfolioRequestAssociatedIndexFund[];
  userCategory: number[];
  status: number;
  currency: number;
  manager: number;
  benchmark: number;
  logoUrl?: string;
  backgroundUrl?: string;
};
export type UpdatePortfolioResponse = ApiResponse<Portfolio>;

/** MAD-5480: a portfolio's Basic and Premium minimum subscription floors. */
export type PortfolioMinimumConfigValues = {
  basicSubscriptionFloor: number | null;
  premiumSubscriptionFloor: number | null;
};

export type PortfolioMinimumConfigFund = {
  indexFundId: number;
  nameEn: string;
  nameAr: string;
  weight: number;
  minimumSubscription: number;
  /** null = never checked against the broker. Shown only; every fund's minimum counts either way. */
  allowFractions: boolean | null;
  amountNeeded: number;
  setsMinimum: boolean;
};

export type PortfolioMinimumConfig = {
  portfolioId: number;
  isCustom: boolean;
  config: PortfolioMinimumConfigValues;
  /** What the funds require (largest minimum ÷ weight, fractional funds included); null for Custom Build. */
  calculatedMinimum: number | null;
  bindingIndexFundId: number | null;
  /** True when saving a floor below calculatedMinimum is rejected. */
  floorCheckApplies: boolean;
  funds: PortfolioMinimumConfigFund[];
};

export type FetchPortfolioMinimumConfigResponse = ApiResponse<PortfolioMinimumConfig>;

export type SavePortfolioMinimumConfigRequest = { id: number } & Partial<PortfolioMinimumConfigValues>;

/**
 * MAD-5480: one saved change to a portfolio's minimum configuration. Values come as text: 'true' / 'false'
 * for toggles, a number for floors and fund limits, null when unset.
 */
export type PortfolioMinimumConfigHistoryEntry = {
  id: number;
  field: keyof PortfolioMinimumConfigValues | string;
  oldValue: string | null;
  newValue: string | null;
  employeeId: number | null;
  employeeEmail: string | null;
  createdAt: string;
};

export type FetchPortfolioMinimumConfigHistoryResponse = ApiResponse<{
  items: PortfolioMinimumConfigHistoryEntry[];
  totalItems: number;
}>;
